import { EmptyState, ErrorState, LoadingState, WithHalfdot } from "@elyxeer/ui";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { FORMAT_ICONS } from "~/components/news/format-icons";
import { NewsCard } from "~/components/news/NewsCard";
import { FacetFilter } from "~/components/protocols/FacetFilter";
import { Screen } from "~/components/ui/Screen";
import { useLocale } from "~/i18n";
import { track } from "~/lib/analytics";
import { fetchNews, fetchNewsFormats, fetchNewsTopics } from "~/lib/api";
import { queryKeys } from "~/lib/query-client";
import { formatLabel, topicLabel } from "~/lib/taxonomy";
import { AnalyticsEventName } from "~/types/analytics";
import {
  CONTENT_FORMATS,
  CONTENT_TOPICS,
  type ContentFormat,
  type ContentTopic,
} from "~/types/news";

const PAGE_SIZE = 12;
const CONTENT_GUTTERS = { xs: 2.5, md: 5 } as const;

/** The search params that hold the two active facets. */
const TOPIC_PARAM = "topic";
const FORMAT_PARAM = "format";

/**
 * Narrow a raw search param to a vocabulary member.
 *
 * A URL is user input, and both facets are closed sets the API will 422 on. Reading
 * a stale `?topic=spanek` link as "no filter" beats rendering an error screen for
 * somebody who bookmarked the library before the vocabulary changed.
 */
function asMember<T extends string>(
  vocabulary: readonly T[],
  value: string | null,
): T | null {
  return value !== null && (vocabulary as readonly string[]).includes(value)
    ? (value as T)
    : null;
}

/**
 * Protocols — the browsable library, filtered on both layers of the taxonomy.
 *
 * The same articles the dashboard teases, read the other way round: the dashboard
 * answers "what is new", this answers "what do you have on sleep, as video". That is
 * why both read the one news endpoint rather than the library getting a table of its
 * own — a protocol *is* an article, a video is an article with a player, and
 * duplicating them would mean writing everything twice.
 *
 * The two selectors compose, which is the point of having two: format is what a piece
 * of content is, topic is what it is about, and the member picks a cell of that grid.
 * The API ANDs them server-side, so the combination is one paged query rather than an
 * intersection assembled here out of two.
 *
 * Both selections live in the URL rather than in component state, the same way the
 * dashboard's calendar does. A member who filters to Regenerace + Video, opens a
 * recording and hits back lands back on that cell and not at the top of the
 * unfiltered library, and the filtered view is a link they can send to someone.
 */
export function ProtocolsRoute() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedTopic = asMember<ContentTopic>(
    CONTENT_TOPICS,
    searchParams.get(TOPIC_PARAM),
  );
  const selectedFormat = asMember<ContentFormat>(
    CONTENT_FORMATS,
    searchParams.get(FORMAT_PARAM),
  );

  /** Write one facet back to the URL, leaving the other one alone. */
  const select = (param: string, value: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (value === null) next.delete(param);
    else next.set(param, value);
    // Replace rather than push: flicking through facets is browsing one screen, not
    // walking a trail, and a pushed entry per chip would bury the way out of the tab.
    setSearchParams(next, { replace: true });
  };

  const selectTopic = (topic: ContentTopic | null) => {
    // The null matches the sentinel the query key uses for "no filter", so a cleared
    // selector is a recorded choice rather than a hole in the data.
    track(AnalyticsEventName.FilterTopicApplied, {
      subjectType: "topic",
      subjectKey: topic,
      // The other facet is carried so the report can tell "chose Video" apart from
      // "narrowed Regenerace to Video". Omitted rather than null when unset: the
      // props bag takes scalars only.
      props: {
        cleared: topic === null,
        ...(selectedFormat !== null ? { with_format: selectedFormat } : {}),
      },
    });
    select(TOPIC_PARAM, topic);
  };

  const selectFormat = (format: ContentFormat | null) => {
    track(AnalyticsEventName.FilterFormatApplied, {
      subjectType: "format",
      subjectKey: format,
      props: {
        cleared: format === null,
        ...(selectedTopic !== null ? { with_topic: selectedTopic } : {}),
      },
    });
    select(FORMAT_PARAM, format);
  };

  // No error branch on either facet list: if they cannot be loaded the selectors
  // simply do not appear and the whole library is still readable below them. A filter
  // that failed to load is a missing convenience, not a broken screen.
  const { data: topicList } = useQuery({
    queryKey: queryKeys.protocolTopics(locale),
    queryFn: ({ signal }) => fetchNewsTopics(locale, signal),
  });

  const { data: formatList } = useQuery({
    queryKey: queryKeys.protocolFormats(locale),
    queryFn: ({ signal }) => fetchNewsFormats(locale, signal),
  });

  const {
    data,
    isPending,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: queryKeys.protocols(locale, selectedTopic, selectedFormat),
    queryFn: ({ pageParam, signal }) =>
      fetchNews({
        locale,
        topic: selectedTopic ?? undefined,
        format: selectedFormat ?? undefined,
        limit: PAGE_SIZE,
        offset: pageParam,
        signal,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const loaded = lastPage.offset + lastPage.items.length;
      return loaded < lastPage.total ? loaded : undefined;
    },
  });

  const articles = data?.pages.flatMap((page) => page.items) ?? [];
  const filtered = selectedTopic !== null || selectedFormat !== null;

  return (
    <Screen maxWidth="lg" disableGutters>
      <Box
        sx={{ display: "flex", flexDirection: "column", gap: { xs: 3, md: 4 } }}
      >
        <Box sx={{ paddingInline: CONTENT_GUTTERS }}>
          <Typography variant="H1" sx={{ color: "text.primary" }}>
            <WithHalfdot size={11}>{t("protocols.title")}</WithHalfdot>
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {formatList ? (
            <FacetFilter<ContentFormat>
              label={t("protocols.formatLabel")}
              allLabel={t("protocols.allFormats")}
              options={formatList.items.map((facet) => ({
                value: facet.format,
                count: facet.count,
              }))}
              selected={selectedFormat}
              onSelect={selectFormat}
              labelFor={(value) => formatLabel(t, value)}
              appearance="tabs"
              iconFor={(value) => FORMAT_ICONS[value]}
              allIcon={GridViewOutlinedIcon}
              testIdPrefix="protocols.format"
            />
          ) : null}

          {topicList ? (
            <FacetFilter<ContentTopic>
              label={t("protocols.topicLabel")}
              allLabel={t("protocols.allTopics")}
              options={topicList.items.map((facet) => ({
                value: facet.topic,
                count: facet.count,
              }))}
              selected={selectedTopic}
              onSelect={selectTopic}
              labelFor={(value) => topicLabel(t, value)}
              appearance="chips"
              testIdPrefix="protocols.topic"
            />
          ) : null}
        </Box>

        <Box
          sx={{
            paddingInline: CONTENT_GUTTERS,
            display: "flex",
            flexDirection: "column",
            gap: { xs: 3, md: 4 },
          }}
        >
          {isPending ? <LoadingState /> : null}

          {isError ? (
            <ErrorState
              message={
                error instanceof Error ? error.message : t("protocols.error")
              }
              onRetry={() => void refetch()}
            />
          ) : null}

          {!isPending && !isError && articles.length === 0 ? (
            <EmptyState
              // A combination that matches nothing is a different situation from an
              // empty library, and the way out of it is to widen the filter.
              message={
                filtered ? t("protocols.emptyFiltered") : t("protocols.empty")
              }
            />
          ) : null}

          {articles.length > 0 ? (
            <Box
              data-testid="protocols.list"
              sx={{
                display: "grid",
                // Same auto-fill rule as the dashboard grid, so a tile is the same
                // width on both screens and the two read as one system.
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(auto-fill, minmax(320px, 1fr))",
                },
                gap: { xs: 2, md: 3 },
              }}
            >
              {articles.map((article) => (
                <NewsCard key={article.id} article={article} showTopics />
              ))}
            </Box>
          ) : null}

          {hasNextPage ? (
            <Button
              variant="outlined"
              onClick={() => void fetchNextPage()}
              disabled={isFetchingNextPage}
              data-testid="protocols.load-more"
              sx={{ alignSelf: { md: "center" }, minWidth: { md: 240 } }}
            >
              {t("protocols.loadMore")}
            </Button>
          ) : null}
        </Box>
      </Box>
    </Screen>
  );
}
